import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  ButtonBase,
  Chip,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ErrorOutline as ErrorIcon,
  WarningAmberOutlined as WarningIcon,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import type { IronPythonRuntimeStatus } from '../../hooks/useIronPythonValidation';
import type { IronPythonDiagnostic } from '../../lib/ironPythonSyntax';
import {
  borderRadius,
  getSemanticSurfaceTokens,
  layoutGap,
} from '../../../../theme/theme';

interface RuntimeGuideItemProps {
  label: string;
  code?: string;
  children: React.ReactNode;
}

interface PythonScriptEditorSidebarProps {
  diagnostics: IronPythonDiagnostic[];
  runtimeStatus: IronPythonRuntimeStatus;
  runtimeError?: string;
  isCompactLayout: boolean;
  onSelectDiagnostic: (diagnostic: IronPythonDiagnostic) => void;
}

const RuntimeGuideItem: React.FC<RuntimeGuideItemProps> = ({ label, code, children }) => {
  const theme = useTheme();
  const colors = getSemanticSurfaceTokens(theme);
  return (
    <Box
      sx={{
        p: 1.25,
        borderRadius: `${borderRadius.md}px`,
        border: `1px solid ${colors.border.default}`,
        backgroundColor: colors.background.card,
      }}
    >
      <Typography variant="caption" sx={{ color: colors.text.tertiary, fontWeight: 700 }}>
        {label}
      </Typography>
      {code && (
        <Box
          component="code"
          sx={{
            display: 'block',
            width: 'fit-content',
            maxWidth: '100%',
            my: 0.5,
            px: 0.75,
            py: 0.375,
            borderRadius: `${borderRadius.sm}px`,
            color: theme.palette.primary.light,
            backgroundColor: alpha(theme.palette.primary.main, 0.1),
            fontSize: '0.75rem',
            overflowWrap: 'anywhere',
          }}
        >
          {code}
        </Box>
      )}
      <Typography variant="caption" sx={{ display: 'block', color: colors.text.secondary }}>
        {children}
      </Typography>
    </Box>
  );
};

const getEmptyProblemsMessage = (
  runtimeStatus: IronPythonRuntimeStatus,
  runtimeError?: string
) => {
  if (runtimeStatus === 'checking') {
    return 'Checking syntax with IronPython 3.4.2…';
  }
  if (runtimeStatus === 'unavailable') {
    return runtimeError || 'Local editor checks passed. The IronPython syntax check is unavailable; the formula has not been executed in Revit.';
  }
  return 'No syntax problems found. Runtime behavior is checked only when PipeEX executes the formula during export.';
};

const ProblemsPanel: React.FC<Omit<PythonScriptEditorSidebarProps, 'isCompactLayout'>> = ({
  diagnostics,
  runtimeStatus,
  runtimeError,
  onSelectDiagnostic,
}) => {
  const theme = useTheme();
  const colors = getSemanticSurfaceTokens(theme);
  return (
    <Box
      component="section"
      aria-labelledby="python-editor-problems-title"
      sx={{
        flexShrink: 0,
        border: `1px solid ${colors.border.default}`,
        borderRadius: `${borderRadius.md}px`,
        backgroundColor: colors.background.card,
        overflow: 'hidden',
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 1.25, py: 1, borderBottom: `1px solid ${colors.border.default}` }}
      >
        <Typography id="python-editor-problems-title" variant="subtitle2" fontWeight={700}>
           Formula diagnostics
        </Typography>
        <Chip label={diagnostics.length} size="small" variant="outlined" />
      </Stack>
      {diagnostics.length === 0 ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', p: 1.25 }}>
          {getEmptyProblemsMessage(runtimeStatus, runtimeError)}
        </Typography>
      ) : (
        <Stack sx={{ maxHeight: 230, overflowY: 'auto' }}>
          {diagnostics.slice(0, 20).map((diagnostic, index) => {
            const isError = diagnostic.severity === 'error';
            const color = isError ? theme.palette.error.main : theme.palette.warning.main;
            return (
              <ButtonBase
                key={`${diagnostic.source}-${diagnostic.line}-${diagnostic.message}-${index}`}
                aria-label={`Go to line ${diagnostic.line}: ${diagnostic.message}`}
                onClick={() => onSelectDiagnostic(diagnostic)}
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1,
                  width: '100%',
                  px: 1.25,
                  py: 1,
                  textAlign: 'left',
                  borderBottom: `1px solid ${colors.border.default}`,
                  '&:hover': { backgroundColor: alpha(color, 0.06) },
                }}
              >
                {isError
                  ? <ErrorIcon fontSize="small" sx={{ color, mt: 0.125 }} />
                  : <WarningIcon fontSize="small" sx={{ color, mt: 0.125 }} />}
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="caption" fontWeight={700} sx={{ color }}>
                    Line {diagnostic.line}{diagnostic.column ? `:${diagnostic.column}` : ''}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {diagnostic.message}
                  </Typography>
                  <Typography variant="caption" sx={{ color: colors.text.tertiary }}>
                    {diagnostic.source === 'ironpython' ? 'IronPython 3.4.2 syntax check' : 'Local editor analysis'}
                  </Typography>
                </Box>
              </ButtonBase>
            );
          })}
        </Stack>
      )}
    </Box>
  );
};

const RuntimeGuide: React.FC<{ defaultExpanded: boolean }> = ({ defaultExpanded }) => (
  <Accordion defaultExpanded={defaultExpanded} disableGutters elevation={0}>
    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
      <Box>
        <Typography variant="subtitle2" fontWeight={700}>Export runtime contract</Typography>
        <Typography variant="caption" color="text.secondary">
          During export, PipeEX calls this function once for each matching Revit element.
        </Typography>
      </Box>
    </AccordionSummary>
    <AccordionDetails>
      <Stack spacing={layoutGap.row}>
        <RuntimeGuideItem label="Required entry point" code="get_elem_property(elem)">
          Keep this exact function name and return the value that PipeEX should write to the PCF attribute.
        </RuntimeGuideItem>
        <RuntimeGuideItem label="Element access" code="elem.Document">
          <code>elem</code> is the current Revit element. Read its document and parameters through the Revit API.
        </RuntimeGuideItem>
        <RuntimeGuideItem label="Safe output" code={'return ""'}>
          Return a string for the PCF attribute. Return an empty string when the source parameter has no usable value.
        </RuntimeGuideItem>
      </Stack>
    </AccordionDetails>
  </Accordion>
);

const PythonScriptEditorSidebar: React.FC<PythonScriptEditorSidebarProps> = (props) => (
  <Stack
    component="aside"
    spacing={layoutGap.row}
    aria-label="PCF formula help and diagnostics"
    sx={{
      minHeight: 0,
      mt: { xs: 1.5, md: 0 },
      overflowY: { xs: 'visible', md: 'auto' },
    }}
  >
    <ProblemsPanel {...props} />
    <RuntimeGuide defaultExpanded={!props.isCompactLayout} />
  </Stack>
);

export default React.memo(PythonScriptEditorSidebar);
